/**
 * Iter 31 (R2) — Suite documentation block.
 * Shows bilingual "What it is / How it works / Who uses it / Expected output"
 * plus two PDF upload slots (user manual + quick reference guide).
 *
 * Visible in both Admin and HM-Consultant portals; editable by hm_admin and
 * hm_advisor (back-end enforces). The page must never look empty: defaults
 * are seeded for all 9 suites.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '@/context/AppContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { FileText, Upload, Download, Trash2, Pencil, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';

const FIELDS = [
  { key: 'what_is',   tKey: 'pages.suiteDocs.whatIs' },
  { key: 'how_works', tKey: 'pages.suiteDocs.howWorks' },
  { key: 'who_uses',  tKey: 'pages.suiteDocs.whoUses' },
  { key: 'output',    tKey: 'pages.suiteDocs.output' },
];

const SLOTS = [
  { key: 'user_manual', tKey: 'pages.suiteDocs.userManual' },
  { key: 'quick_ref',   tKey: 'pages.suiteDocs.quickRef' },
];

export default function SuiteDocSection({ suiteCode }) {
  const { t, i18n } = useTranslation();
  const { user } = useApp();
  const isES = (i18n.language || '').startsWith('es');
  const canEdit = user?.role === 'hm_admin' || user?.role === 'hm_advisor';
  const [doc, setDoc] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!suiteCode) return;
    adminApi.getSuiteDoc(suiteCode).then(setDoc).catch(() => setDoc(null));
  }, [suiteCode]);

  const openEdit = () => {
    setDraft({
      what_is_en:   doc?.what_is_en || '',   what_is_es:   doc?.what_is_es || '',
      how_works_en: doc?.how_works_en || '', how_works_es: doc?.how_works_es || '',
      who_uses_en:  doc?.who_uses_en || '',  who_uses_es:  doc?.who_uses_es || '',
      output_en:    doc?.output_en || '',    output_es:    doc?.output_es || '',
    });
    setEditing(true);
  };

  const saveText = async () => {
    setSaving(true);
    try {
      const updated = await adminApi.patchSuiteDoc(suiteCode, draft);
      setDoc(updated);
      setEditing(false);
      toast.success(t('pages.suiteDocs.contentSaved'));
    } catch (e) {
      toast.error(t('pages.suiteDocs.uploadFailed'));
    } finally { setSaving(false); }
  };

  const valFor = (key) => {
    const en = doc?.[`${key}_en`] || '';
    const es = doc?.[`${key}_es`] || '';
    return isES ? (es || en) : (en || es);
  };

  if (!doc) {
    return (
      <Card className="p-6 shadow-card text-sm text-muted-foreground" data-testid={`suite-docs-${suiteCode}-loading`}>
        {t('common.loading', { defaultValue: 'Loading…' })}
      </Card>
    );
  }

  return (
    <Card className="p-6 shadow-card" data-testid={`suite-docs-${suiteCode}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <h3 className="font-display font-bold text-lg tracking-tight">{t('pages.suiteDocs.title')}</h3>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{t('pages.suiteDocs.subtitle')}</p>
        </div>
        {canEdit && !editing && (
          <Button size="sm" variant="outline" onClick={openEdit} data-testid={`suite-docs-${suiteCode}-edit`}>
            <Pencil className="h-3.5 w-3.5 mr-1.5" />{t('pages.suiteDocs.edit')}
          </Button>
        )}
      </div>

      {!editing ? (
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t(f.tKey)}</p>
              <p className="text-sm leading-relaxed mt-1.5 whitespace-pre-line">{valFor(f.key) || '—'}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {FIELDS.map((f) => (
            <div key={f.key} className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t(f.tKey)} (EN)</label>
                <Textarea data-testid={`suite-docs-${suiteCode}-${f.key}-en`} rows={2}
                          value={draft[`${f.key}_en`] || ''}
                          onChange={(e) => setDraft((d) => ({ ...d, [`${f.key}_en`]: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t(f.tKey)} (ES)</label>
                <Textarea data-testid={`suite-docs-${suiteCode}-${f.key}-es`} rows={2}
                          value={draft[`${f.key}_es`] || ''}
                          onChange={(e) => setDraft((d) => ({ ...d, [`${f.key}_es`]: e.target.value }))} />
              </div>
            </div>
          ))}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(false)} data-testid={`suite-docs-${suiteCode}-cancel`}>
              <X className="h-3.5 w-3.5 mr-1.5" />{t('common.cancel')}
            </Button>
            <Button size="sm" onClick={saveText} disabled={saving} data-testid={`suite-docs-${suiteCode}-save`} className="bg-gradient-primary">
              <Save className="h-3.5 w-3.5 mr-1.5" />{saving ? t('common.saving') : t('pages.suiteDocs.saveText')}
            </Button>
          </div>
        </div>
      )}

      {/* File slots */}
      <div className="mt-6 pt-6 border-t border-border/60">
        <p className="text-xs text-muted-foreground leading-relaxed mb-3" data-testid={`suite-docs-${suiteCode}-ownership-note`}>
          {t('pages.suiteDocs.ownership', { defaultValue: 'These documents are part of the standard suite package and are provided by Corporate Talent Hub. They are optional: if not uploaded yet, the Corporate Talent Hub team will publish them here — clients do not need to upload anything.' })}
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {SLOTS.map((s) => (
            <DocSlot key={s.key} suiteCode={suiteCode} slot={s.key} label={t(s.tKey)} doc={doc} setDoc={setDoc} canEdit={canEdit} />
          ))}
        </div>
      </div>
    </Card>
  );
}

function DocSlot({ suiteCode, slot, label, doc, setDoc, canEdit }) {
  const { t } = useTranslation();
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const file = doc?.[slot];

  const onPick = () => inputRef.current?.click();
  const onChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    try {
      const updated = await adminApi.uploadSuiteDocFile(suiteCode, slot, f);
      setDoc(updated);
      toast.success(t('pages.suiteDocs.uploaded'));
    } catch (err) {
      toast.error(t('pages.suiteDocs.uploadFailed'));
    } finally { setBusy(false); }
  };

  const onDownload = async () => {
    try {
      const token = localStorage.getItem('cth_token');
      const url = adminApi.suiteDocDownloadUrl(suiteCode, slot);
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = file?.filename || `${suiteCode}-${slot}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      toast.error(t('pages.suiteDocs.uploadFailed'));
    }
  };

  const onRemove = async () => {
    setBusy(true);
    try {
      const updated = await adminApi.deleteSuiteDocFile(suiteCode, slot);
      setDoc(updated);
    } catch { /* no-op */ }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-4 border-dashed border-2 bg-muted/20" data-testid={`suite-docs-${suiteCode}-slot-${slot}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold">{label}</p>
          {file ? (
            <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
              <FileText className="h-3.5 w-3.5" />
              <span className="truncate" data-testid={`suite-docs-${suiteCode}-${slot}-filename`}>{file.filename}</span>
              <Badge variant="outline" className="text-xs">{Math.round((file.size || 0) / 1024)} KB</Badge>
            </div>
          ) : (
            <p className="text-xs italic text-muted-foreground mt-1.5" data-testid={`suite-docs-${suiteCode}-${slot}-empty`}>
              {t('pages.suiteDocs.noFileYet')}
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {file && (
          <Button size="sm" variant="outline" onClick={onDownload} data-testid={`suite-docs-${suiteCode}-${slot}-download`}>
            <Download className="h-3.5 w-3.5 mr-1.5" />{t('pages.suiteDocs.download')}
          </Button>
        )}
        {canEdit && (
          <>
            <Button size="sm" onClick={onPick} disabled={busy} data-testid={`suite-docs-${suiteCode}-${slot}-upload`}>
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              {busy ? t('pages.suiteDocs.uploading') : file ? t('pages.suiteDocs.replace') : t('pages.suiteDocs.upload')}
            </Button>
            {file && (
              <Button size="sm" variant="ghost" onClick={onRemove} disabled={busy} data-testid={`suite-docs-${suiteCode}-${slot}-remove`}>
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />{t('pages.suiteDocs.remove')}
              </Button>
            )}
            <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onChange} />
          </>
        )}
      </div>
    </Card>
  );
}
